// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: long pastes. A paste that would be a wall of text becomes a text file sent with the message,
// and the text keeps a marker line where it was pasted: `[Pasted text: pasted-text-1.txt]`. Composers
// show the marker as a folded block (ComposerPastedTextNode); messages and agent prompts show the same
// fold in its place, opening the shared file preview. Apps without this read the marker as plain text
// next to the attached file, and agents read it as the name of the file it stands for.

/** Pastes longer than this many lines, or characters, fold into a file. */
export const PASTED_TEXT_FOLD_LINES = 40;
export const PASTED_TEXT_FOLD_CHARS = 2500;

const NAME_RE = /^pasted-text-(\d+)\.txt$/;
const MARKER_RE = /^\[Pasted text: (pasted-text-\d+\.txt)\]$/;
const MARKER_LINE_RE = /^\[Pasted text: (pasted-text-\d+\.txt)\][ \t]*$/gm;

export type PastedTextPart = {kind: 'text'; text: string} | {kind: 'paste'; name: string};

export function isPastedTextName(name: string): boolean {
	return NAME_RE.test(name);
}

/** The line that stands for a pasted file in the text. */
export function pastedTextMarker(name: string): string {
	return `[Pasted text: ${name}]`;
}

/** The file name a marker line names, or null when the line is not a marker. */
export function pastedTextMarkerName(line: string): string | null {
	return MARKER_RE.exec(line.trim())?.[1] ?? null;
}

export function countLines(text: string): number {
	if (!text) return 0;
	let lines = 1;
	for (let index = text.indexOf('\n'); index !== -1; index = text.indexOf('\n', index + 1)) lines += 1;
	return text.endsWith('\n') ? lines - 1 : lines;
}

/** Whether a paste folds: too long to read in a message, or longer than the message can be. */
export function shouldFoldPastedText(text: string, maxLength?: number): boolean {
	if (!text.trim()) return false;
	if (maxLength !== undefined && text.length > maxLength) return true;
	return text.length > PASTED_TEXT_FOLD_CHARS || countLines(text) > PASTED_TEXT_FOLD_LINES;
}

/** The next free name, `pasted-text-1.txt`, `pasted-text-2.txt`, ... beside the files already there. */
export function nextPastedTextName(taken: Iterable<string>): string {
	let highest = 0;
	for (const name of taken) {
		const match = NAME_RE.exec(name);
		if (match) highest = Math.max(highest, Number(match[1]));
	}
	return `pasted-text-${highest + 1}.txt`;
}

export function pastedTextFile(text: string, name: string): File {
	return new File([text], name, {type: 'text/plain'});
}

/** The pasted files a text still has markers for. */
export function pastedTextNames(text: string): Set<string> {
	const names = new Set<string>();
	if (!text.includes('[Pasted text: ')) return names;
	for (const match of text.matchAll(MARKER_LINE_RE)) names.add(match[1]);
	return names;
}

/**
 * The text cut at its marker lines. Only markers for `files` (the names the message really carries)
 * count; any other marker stays text. Text parts keep their own line breaks, trimmed at the cuts.
 */
export function splitPastedText(text: string, files: ReadonlySet<string>): Array<PastedTextPart> {
	if (files.size === 0 || !text.includes('[Pasted text: ')) return text ? [{kind: 'text', text}] : [];
	const parts: Array<PastedTextPart> = [];
	let rest = 0;
	const push = (chunk: string) => {
		const trimmed = chunk.replace(/^\n+|\n+$/g, '');
		if (trimmed.trim()) parts.push({kind: 'text', text: trimmed});
	};
	for (const match of text.matchAll(MARKER_LINE_RE)) {
		if (!files.has(match[1])) continue;
		push(text.slice(rest, match.index));
		parts.push({kind: 'paste', name: match[1]});
		rest = match.index + match[0].length;
	}
	push(text.slice(rest));
	return parts;
}

/** For one-line previews (notifications, chat rows): each marker becomes its file's name. */
export function withPastedTextFileNames(text: string): string {
	if (!text.includes('[Pasted text: ')) return text;
	return text.replace(MARKER_LINE_RE, '$1');
}
